import type { AssetClass, Exchange } from '@/types/generated/common/types'

// Mirrors `MarketType` in `watchlist-wizard.tsx`, re-declared locally on
// purpose: that file imports this one, so importing the type back would be
// a circular dependency. It's a small, stable literal union — safe to keep
// in sync by hand.
export type MarketType = 'perpetual' | 'spot' | 'future' | 'options'

export type FilterCandidate =
  | { dimension: 'exchange'; value: Exchange; label: string }
  | { dimension: 'type'; value: MarketType; label: string }
  | { dimension: 'assetClass'; value: AssetClass; label: string }

const DIMENSION_RANK: Record<FilterCandidate['dimension'], number> = {
  exchange: 0,
  type: 1,
  assetClass: 2,
}

export function buildFilterCandidates(
  exchanges: { exchange: Exchange; label: string }[],
  types: { key: MarketType; label: string }[],
  assetClasses: { assetClass: AssetClass; label: string }[]
): FilterCandidate[] {
  return [
    ...exchanges.map(
      ({ exchange, label }): FilterCandidate => ({ dimension: 'exchange', value: exchange, label })
    ),
    ...types.map(({ key, label }): FilterCandidate => ({ dimension: 'type', value: key, label })),
    ...assetClasses.map(
      ({ assetClass, label }): FilterCandidate => ({
        dimension: 'assetClass',
        value: assetClass,
        label,
      })
    ),
  ]
}

// Case-insensitive prefix match, ranked Exchange -> Type -> Asset class,
// then alphabetically within a dimension. An empty prefix (just typed "@")
// matches every candidate, which is intentional — see the design spec.
export function matchCandidates(candidates: FilterCandidate[], prefix: string): FilterCandidate[] {
  const normalized = prefix.trim().toLowerCase()
  return candidates
    .filter((candidate) => candidate.label.toLowerCase().startsWith(normalized))
    .sort(
      (a, b) =>
        DIMENSION_RANK[a.dimension] - DIMENSION_RANK[b.dimension] || a.label.localeCompare(b.label)
    )
}

export interface ActiveAtToken {
  start: number
  prefix: string
}

// Finds the `@token` the caret is actively typing, if any. Only matches
// when the caret sits at the tail of a contiguous non-whitespace run that
// starts with '@' — ghost-completion applies to what you're typing *right
// now*, not to some `@token` you've since moved the cursor away from.
export function findActiveAtToken(text: string, caretIndex: number): ActiveAtToken | null {
  let start = caretIndex
  while (start > 0 && !/\s/.test(text[start - 1])) start -= 1
  if (text[start] !== '@') return null
  if (caretIndex < text.length && !/\s/.test(text[caretIndex])) return null
  return { start, prefix: text.slice(start + 1, caretIndex) }
}

// Removes the `[start, end)` token from `text` and collapses the single
// space around it, if any, so committing a pill doesn't leave "BTC  ETH"
// (two spaces) behind.
export function removeAtToken(text: string, start: number, end: number): string {
  const before = text.slice(0, start)
  const after = text.slice(end)
  if (before.endsWith(' ') && after.startsWith(' ')) return before + after.slice(1)
  return before + after
}
